import React, { useEffect } from 'react';
import {
  ListItem,
  List,
  ListItemAvatar,
  Avatar,
  ListItemSecondaryAction,
  ListItemText,
  Container,
  IconButton,
  Typography,
  Tooltip,
  ListSubheader,
  Fab,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import noImg from '../../assets/images/avatar-no-img.png';
import { useSelector, useDispatch } from 'react-redux';
import { isLoaded, isEmpty } from 'react-redux-firebase';
import { Link } from 'react-router-dom';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';
import { updateUi } from '../../redux/actions/uiActions';
import { actions } from '../../redux/actions/actionsHandler';
import { useIntl } from 'react-intl';
import { fetchProfileImages } from '../../redux/actions/userActions';
import { isObject } from 'lodash';

const useStyles = makeStyles((theme) => ({
  container: { ...theme.container(2), marginLeft: 0 },
  listContainer: {
    maxWidth: theme.spacing(75),
    marginBottom: theme.spacing(2),
  },
  listItem: {
    paddingRight: theme.spacing(15),
    margin: theme.spacing(2),
  },
  itemText: {
    margin: theme.spacing(1, 1),
  },
  avatar: {
    width: theme.spacing(8),
    height: theme.spacing(8),
    padding: theme.spacing(0.5),
  },
  skeletonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignContent: 'flex-start',
  },
  skeletonItemText: {
    minWidth: theme.spacing(30),
    margin: theme.spacing(0, 1),
    height: theme.spacing(8),
  },
}));

const permissionTypes = ['pilot', 'checker', 'assistant', 'moderator'];

export default function ManageUsers(props) {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const intl = useIntl();

  const { userProfiles, managerUid, user, company } = useSelector((s) => ({
    managerUid: s.firebase.auth.uid,
    userProfiles: s.firestore.data.userProfiles,
    user: s.user,
    company: s.firebase.profile?.company,
  }));

  useEffect(() => {
    if (!!company && isLoaded(userProfiles) && isObject(userProfiles)) {
      // Child user profiles to be fetched
      dispatch(fetchProfileImages({ profileUids: Object.keys(userProfiles), company }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfiles]);

  const showRemoveUserDialogConfirmation = (uid) => {
    const userData = userProfiles[uid];
    if (!userData) {
      return;
    }

    dispatch(
      updateUi({
        dialog: {
          title: intl.formatMessage({ id: 'manUsr.remUserTitle' }),
          message: intl.formatMessage(
            { id: 'manUsr.remUserMsg' },
            { name: <b key="mub0">{`${userData.firstName} ${userData.lastName}`}</b>, nl: <br key="munl0" /> }
          ),
          confirmAction: actions.REMOVE_USER,
          actionData: { uid },
          show: true,
        },
      })
    );
  };

  return (
    <Container className={classes.container}>
      <Typography variant="h4" style={theme.text.title}>
        {intl.formatMessage({ id: 'manUsr.title' })}
      </Typography>
      <Typography variant="subtitle1" style={theme.text.subtitle}>
        {intl.formatMessage({ id: 'manUsr.subtitle' })}
      </Typography>
      <List
        className={classes.listContainer}
        subheader={<ListSubheader>{intl.formatMessage({ id: 'manUsr.users' })}</ListSubheader>}
      >
        {isLoaded(userProfiles) ? (
          !isEmpty(userProfiles) ? (
            Object.keys(userProfiles).map((uid, index) => {
              const profile = userProfiles[uid];
              const profileImageThumb = user.profilePicThumbs[uid];
              const labelId = `label${uid}`;

              if (!profile || managerUid === uid) {
                return null;
              }

              const permissions = [];
              permissionTypes.forEach((permission) => {
                if (profile && profile[permission]) {
                  permissions.push(intl.formatMessage({ id: permission }));
                }
              });

              return (
                <ListItem key={uid} button alignItems="flex-start" className={classes.listItem}>
                  <ListItemAvatar>
                    {profileImageThumb && profileImageThumb.isLoaded ? (
                      <Avatar
                        alt={intl.formatMessage({ id: 'altUsrProfImage' })} //todo change
                        src={profileImageThumb.src || noImg}
                        className={classes.avatar}
                      />
                    ) : (
                      <Skeleton variant="circle" className={classes.avatar} animation="wave" />
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    id={labelId}
                    className={classes.itemText}
                    primary={`${profile.firstName} ${profile.lastName}`}
                    secondary={intl.formatMessage(
                      { id: 'manUsr.userDesc' },
                      {
                        domains: profile.authDomains ? profile.authDomains.join(', ') : '',
                        nl: <br key={`${uid}nl`} />,
                        perm: permissions ? permissions.join(', ') : '',
                      }
                    )}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Edit">
                      <IconButton
                        component={Link}
                        to={`/profile/${uid}`}
                        edge="start"
                        aria-label="Edit"
                        style={{ color: theme.palette.info.main }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={intl.formatMessage({ id: 'remove' })}>
                      <IconButton
                        edge="end"
                        aria-label={intl.formatMessage({ id: 'remove' })}
                        style={{ color: theme.palette.error.main }}
                        onClick={() => showRemoveUserDialogConfirmation(uid)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })
          ) : (
            <ListItem key="emptyUsers" alignItems="flex-start" className={classes.listItem}>
              <ListItemText
                id="emptyUsers"
                className={classes.itemText}
                primary={intl.formatMessage({ id: 'manUsr.noUsers' })}
              />
            </ListItem>
          )
        ) : (
          [0, 1, 2].map((i) => (
            <Container className={clsx(classes.listItem, classes.skeletonContainer)} key={`skel${i}`}>
              <Skeleton variant="circle" animation="wave" className={classes.avatar} />
              <Skeleton variant="text" animation="wave" className={classes.skeletonItemText} />
            </Container>
          ))
        )}

        <ListItem />
        <ListItem>
          <Fab variant="extended" size="medium" color="secondary" aria-label="add" component={Link} to="/adduser">
            <AddCircleIcon style={{ marginRight: theme.spacing(1) }} />
            {intl.formatMessage({ id: 'manUsr.addUsr' })}
          </Fab>
        </ListItem>
      </List>
    </Container>
  );
}
